import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '../store/authStore';

let socket: Socket | null = null;

export const getSocket = (): Socket => {
  if (!socket) {
    // WS host resolution: socket.io lives on the API origin
    // (`api.hauraki.ai/socket.io/*` per the Caddy config), NOT the SPA
    // origin. PR #28's CI step bakes REACT_APP_API_URL=https://api.hauraki.ai
    // into the bundle; we derive WS from that so a separate REACT_APP_WS_URL
    // isn't required.
    //
    // Prod bug this fixes: window.location.origin resolves to the app's own
    // domain (https://learn.hauraki.ai), which has no /socket.io route → handshake
    // failed → the reconnect loop spammed connect_error indefinitely,
    // showing the "reconnecting…" indicator and burning rate-limit budget
    // through the Caddy 404s.
    //
    // socket.io-client auto-upgrades the protocol (https → wss) at
    // connect time, so passing the https API URL is correct.
    const wsUrl =
      process.env.REACT_APP_WS_URL ||
      process.env.REACT_APP_API_URL ||
      window.location.origin;
    // Reconnect strategy:
    //   - Infinity attempts so a long network blip doesn't permanently dead
    //     the socket (the prior 5-attempt cap meant students had to refresh
    //     after ~5s outage, losing IDE state).
    //   - Exponential backoff up to 30s between attempts so we don't spam a
    //     downed server while the user's tab is open.
    //   - randomizationFactor adds jitter to avoid thundering-herd on recovery.
    socket = io(wsUrl, {
      path: '/socket.io',
      // Re-read the token on EVERY (re)connection attempt instead of capturing
      // it once at socket creation. A socket built before ApiBridge mirrors the
      // OIDC access token into authStore (a fresh-load race, e.g. deep-linking
      // straight to /lab/:id) would otherwise send auth:{token:null} and
      // ws-gateway rejects it — and because reconnects reuse the same static
      // auth object, it never recovers ("Authentication failed" + connect loop).
      auth: (cb: (data: object) => void) => {
        cb({ token: useAuthStore.getState().token });
      },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30_000,
      randomizationFactor: 0.5,
    });

    socket.on('connect', () => {
      console.log('WebSocket connected');
    });

    socket.on('disconnect', () => {
      console.log('WebSocket disconnected');
    });

    socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      // If the server rejected the handshake for auth reasons, retrying with
      // the same token will never succeed — the JWT is revoked or expired.
      // Stop the reconnect loop and trigger the same soft-redirect path the
      // HTTP 401 interceptor uses, so the user lands at /login without losing
      // their IDE state to a hard refresh.
      const message = (error?.message || '').toLowerCase();
      const isAuthFailure =
        message.includes('authentication') ||
        message.includes('session revoked') ||
        message.includes('unauthorized') ||
        message.includes('jwt');
      if (isAuthFailure) {
        try {
          socket?.disconnect();
        } catch {
          /* ignore */
        }
        socket = null;
        try {
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        } catch {
          /* ignore */
        }
      }
    });
  }

  return socket;
};

export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};

// Terminal helpers
export const terminalConnect = (environmentId: string) => {
  const s = getSocket();
  s.emit('terminal:connect', { environmentId });
};

export const terminalInput = (environmentId: string, input: string) => {
  const s = getSocket();
  s.emit('terminal:input', { environmentId, input });
};

export const terminalResize = (environmentId: string, cols: number, rows: number) => {
  const s = getSocket();
  s.emit('terminal:resize', { environmentId, cols, rows });
};

export const terminalDisconnect = (environmentId: string) => {
  const s = getSocket();
  s.emit('terminal:disconnect', { environmentId });
};

export const fileRead = (environmentId: string, path: string) => {
  const s = getSocket();
  s.emit('file:read', { environmentId, path });
};

// Code execution. `path` (the file's workspace-relative path) lets the gateway
// write the editor content to its real location and run it as a module, so
// package-relative imports resolve; omit it and the gateway runs inline.
export const codeExecute = (
  environmentId: string,
  code: string,
  language: string,
  path?: string
) => {
  const s = getSocket();
  s.emit('code:execute', { environmentId, code, language, path });
};

// ===================================
// Auto-save and Persistence Helpers
// ===================================

interface FileState {
  path: string;
  content: string;
  cursorPosition?: { line: number; column: number };
  scrollPosition?: { top: number; left: number };
}

// Request state recovery for a lab
export const requestRecovery = (labId: string) => {
  const s = getSocket();
  s.emit('recovery:request', { labId });
};

// Get snapshot history for a file
export const getSnapshotHistory = (labId: string, filePath: string, limit?: number) => {
  const s = getSocket();
  s.emit('snapshots:history', { labId, filePath, limit });
};

// Restore from a specific snapshot
export const restoreSnapshot = (snapshotId: string) => {
  const s = getSocket();
  s.emit('snapshots:restore', { snapshotId });
};

// ===================================
// Session Management Helpers
// ===================================

// Start or resume an IDE session
export const startSession = (labId: string, environmentId: string) => {
  const s = getSocket();
  s.emit('session:start', { labId, environmentId });
};

// Update session state (open files, active file, layout, etc.)
export const updateSession = (
  sessionId: string,
  updates: {
    openFiles?: string[];
    activeFile?: string | null;
    terminalHistory?: string[];
    panelLayout?: {
      instructionsWidth?: number;
      terminalHeight?: number;
      instructionsCollapsed?: boolean;
      terminalCollapsed?: boolean;
    };
    breakpoints?: Array<{ file: string; line: number }>;
  }
) => {
  const s = getSocket();
  s.emit('session:update', { sessionId, updates });
};

// Send session heartbeat
export const sessionHeartbeat = (sessionId: string) => {
  const s = getSocket();
  s.emit('session:heartbeat', { sessionId });
};

// End session
export const endSession = (sessionId: string) => {
  const s = getSocket();
  s.emit('session:end', { sessionId });
};

// ===================================
// Analytics Tracking Helpers
// ===================================

export enum ActivityEventType {
  FILE_OPEN = 'file_open',
  FILE_SAVE = 'file_save',
  FILE_CLOSE = 'file_close',
  CODE_RUN = 'code_run',
  CODE_ERROR = 'code_error',
  TERMINAL_COMMAND = 'terminal_command',
  HINT_VIEW = 'hint_view',
  TASK_COMPLETE = 'task_complete',
  SESSION_START = 'session_start',
  SESSION_END = 'session_end',
  SESSION_RECOVERED = 'session_recovered',
  SESSION_RECOVERY_DISMISSED = 'session_recovery_dismissed',
  LAB_SUBMIT = 'lab_submit',
  LAB_COMPLETE = 'lab_complete',
}

// Track an activity event
export const trackEvent = (
  sessionId: string | null,
  eventType: ActivityEventType,
  eventData: Record<string, any>
) => {
  const s = getSocket();
  s.emit('analytics:event', { sessionId, eventType, eventData });
};

// ===================================
// Event Listeners for Persistence
// ===================================

export const onRecoveryState = (callback: (data: { files: FileState[] }) => void) => {
  const s = getSocket();
  s.on('recovery:state', callback);
  return () => s.off('recovery:state', callback);
};

export const onRecoveryError = (callback: (data: { error: string }) => void) => {
  const s = getSocket();
  s.on('recovery:error', callback);
  return () => s.off('recovery:error', callback);
};

export const onSessionStarted = (callback: (data: { sessionId: string }) => void) => {
  const s = getSocket();
  s.on('session:started', callback);
  return () => s.off('session:started', callback);
};

export const onSessionEnded = (callback: (data: { sessionId: string }) => void) => {
  const s = getSocket();
  s.on('session:ended', callback);
  return () => s.off('session:ended', callback);
};

export const onSnapshotHistory = (callback: (data: { filePath: string; history: any[] }) => void) => {
  const s = getSocket();
  s.on('snapshots:history', callback);
  return () => s.off('snapshots:history', callback);
};

export const onSnapshotRestored = (callback: (data: { file: FileState }) => void) => {
  const s = getSocket();
  s.on('snapshots:restored', callback);
  return () => s.off('snapshots:restored', callback);
};
